import { getDatabase, ref, set, push, remove } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytesResumable, deleteObject, getDownloadURL, UploadTask } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';

export interface FileDocument {
    key: string;
    name: string;
    documentType: 'file';
    category: string;
    fileType: string;
    storageFilename: string;
    fileSize: number;
    uploadedAt: string;
    uploadedBy: string;
    lastModified: string;
    lastModifiedBy?: string;
}

export interface TextDocument {
    key: string;
    name: string;
    documentType: 'text';
    category: string;
    textContent: string;
    uploadedAt: string;
    uploadedBy: string;
    lastModified: string;
    lastModifiedBy?: string;
}

export type DocumentModel = FileDocument | TextDocument;

export interface UploadProgress {
    bytesTransferred: number;
    totalBytes: number;
    progress: number;
}

export type UploadProgressCallback = (progress: UploadProgress) => void;

class BuildingDocumentsApi {
    private db = getDatabase();
    private storage = getStorage();

    getStoragePath(buildingKey: string, storageFilename: string): string {
        return `buildings/${buildingKey}/documents/${storageFilename}`;
    }

    private getDbPath(buildingKey: string, docKey?: string): string {
        if (docKey) {
            return `buildings/${buildingKey}/documents/${docKey}`;
        }
        return `buildings/${buildingKey}/documents`;
    }

    private generateUniqueFileName(originalName: string): string {
        const lastDotIndex = originalName.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalName.slice(0, lastDotIndex) : originalName;
        const extension = lastDotIndex !== -1 ? originalName.slice(lastDotIndex) : '';
        const randomString = Math.random().toString(36).substring(2, 7);
        return `${nameWithoutExt}_${randomString}${extension}`;
    }

    private getFileExtension(filename: string): string {
        const lastDotIndex = filename.lastIndexOf('.');
        return lastDotIndex !== -1 ? filename.slice(lastDotIndex + 1) : '';
    }

    /**
     * Upload a single file document with progress tracking
     */
    async uploadDocument(
        buildingKey: string,
        file: File,
        name: string,
        category: string,
        onProgress?: UploadProgressCallback
    ): Promise<FileDocument> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const documentsRef = ref(this.db, this.getDbPath(buildingKey));
        const newDocRef = push(documentsRef);

        if (!newDocRef.key) {
            throw new Error('Kunde inte skapa dokumentnyckel');
        }

        const uniqueFileName = this.generateUniqueFileName(file.name);
        const storagePath = this.getStoragePath(buildingKey, uniqueFileName);
        const fileRef = storageRef(this.storage, storagePath);

        // Upload to storage with progress tracking
        const uploadTask = uploadBytesResumable(fileRef, file);

        await new Promise<void>((resolve, reject) => {
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    if (onProgress) {
                        onProgress({
                            bytesTransferred: snapshot.bytesTransferred,
                            totalBytes: snapshot.totalBytes,
                            progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        });
                    }
                },
                (error) => reject(error),
                () => resolve()
            );
        });

        const now = formatDateForStorage(new Date());
        const document: FileDocument = {
            key: newDocRef.key,
            name,
            documentType: 'file',
            category,
            fileType: this.getFileExtension(file.name),
            storageFilename: uniqueFileName,
            fileSize: file.size,
            uploadedAt: now,
            uploadedBy: user.uid,
            lastModified: now,
        };

        // Save to database - if this fails, we have an orphaned file
        try {
            await set(newDocRef, document);
        } catch (error) {
            // Rollback: delete the uploaded file
            try {
                await deleteObject(fileRef);
            } catch (cleanupError) {
                console.warn('Failed to cleanup orphaned storage file:', cleanupError);
            }
            throw error;
        }
        recordActivity('document.uploaded', { buildingKey, targetKey: newDocRef.key });

        return document;
    }

    /**
     * Update an existing file document's metadata
     */
    async updateDocument(
        buildingKey: string,
        docKey: string,
        currentDoc: FileDocument,
        updates: { name?: string; category?: string }
    ): Promise<FileDocument> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const updatedDoc: FileDocument = {
            ...currentDoc,
            name: updates.name ?? currentDoc.name,
            category: updates.category ?? currentDoc.category,
            lastModified: formatDateForStorage(new Date()),
            lastModifiedBy: user.uid
        };

        const docRef = ref(this.db, this.getDbPath(buildingKey, docKey));
        await set(docRef, updatedDoc);
        recordActivity('document.updated', { buildingKey, targetKey: docKey });

        return updatedDoc;
    }

    /**
     * Delete a file document (soft delete — moves the metadata node to a
     * tombstone at deletedFiles/{key}; the Storage blob is left for a future
     * cleanup job)
     */
    async deleteDocument(
        buildingKey: string,
        docKey: string,
        storageFilename: string
    ): Promise<void> {
        await softDeleteFile({
            originalPath: this.getDbPath(buildingKey, docKey),
            entityType: 'buildingDocument',
            // Documents store storageFilename, not storagePath. Text documents
            // have no storage object at all — tombstone with empty storagePath
            // (the textContent is preserved in the tombstone metadata).
            resolveStoragePath: (metadata) =>
                metadata.documentType === 'text' || !storageFilename
                    ? ''
                    : this.getStoragePath(buildingKey, storageFilename),
        });
    }

    /**
     * Create a new text document
     */
    async createTextDocument(
        buildingKey: string,
        title: string,
        content: string,
        category: string
    ): Promise<TextDocument> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const documentsRef = ref(this.db, this.getDbPath(buildingKey));
        const newDocRef = push(documentsRef);

        if (!newDocRef.key) {
            throw new Error('Kunde inte skapa dokumentnyckel');
        }

        const now = formatDateForStorage(new Date());
        const document: TextDocument = {
            key: newDocRef.key,
            name: title,
            documentType: 'text',
            category,
            textContent: content,
            uploadedAt: now,
            uploadedBy: user.uid,
            lastModified: now,
            lastModifiedBy: user.uid
        };

        await set(newDocRef, document);
        recordActivity('document.created', { buildingKey, targetKey: newDocRef.key });
        return document;
    }

    /**
     * Update an existing text document
     */
    async updateTextDocument(
        buildingKey: string,
        docKey: string,
        currentDoc: TextDocument,
        updates: { name?: string; textContent?: string; category?: string }
    ): Promise<TextDocument> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const updatedDoc: TextDocument = {
            ...currentDoc,
            name: updates.name ?? currentDoc.name,
            textContent: updates.textContent ?? currentDoc.textContent,
            category: updates.category ?? currentDoc.category,
            lastModified: formatDateForStorage(new Date()),
            lastModifiedBy: user.uid
        };

        const docRef = ref(this.db, this.getDbPath(buildingKey, docKey));
        await set(docRef, updatedDoc);
        recordActivity('document.updated', { buildingKey, targetKey: docKey });

        return updatedDoc;
    }

    /**
     * Delete a text document
     */
    async deleteTextDocument(buildingKey: string, docKey: string): Promise<void> {
        const docRef = ref(this.db, this.getDbPath(buildingKey, docKey));
        await remove(docRef);
        recordActivity('document.deleted', { buildingKey, targetKey: docKey });
    }

    /**
     * Get download URL for a document
     */
    async getDocumentUrl(buildingKey: string, storageFilename: string): Promise<string> {
        const storagePath = this.getStoragePath(buildingKey, storageFilename);
        const fileRef = storageRef(this.storage, storagePath);
        return getDownloadURL(fileRef);
    }

    /**
     * Get download URL from any storage path
     */
    async getUrlFromStoragePath(storagePath: string): Promise<string> {
        const fileRef = storageRef(this.storage, storagePath);
        return getDownloadURL(fileRef);
    }

    /**
     * Download a file by creating a temporary link
     */
    async downloadDocument(url: string, filename: string): Promise<void> {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Download text content as a file
     */
    downloadTextDocument(content: string, filename: string): void {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export const buildingDocumentsApi = new BuildingDocumentsApi();
